import { clamp, noop, resolveElement, resolveElements } from '@jxs/utilities';

/** @type {Window & { gsap?: any, ScrollTrigger?: any }} */
const win = window;

export const gsap = win.gsap;
export const ScrollTrigger = win.ScrollTrigger;

if (gsap && ScrollTrigger) {
  gsap.registerPlugin(ScrollTrigger);
}

class JxsAnimate {
  /**
   * 判断 GSAP 与 ScrollTrigger 是否已经可用。
   *
   * @returns {boolean}
   */
  isAvailable() {
    return Boolean(gsap && ScrollTrigger);
  }

  /**
   * 让目标元素执行轻微上浮动画。
   *
   * 如果传入数组、NodeList 或 HTMLCollection，会按 stagger 间隔依次上浮。
   *
   * @param {Object} [options={}] 配置项。
   * @param {Element|string|Array<Element|string>|NodeListOf<Element>|HTMLCollection} [options.el] 需要执行动画的 DOM 元素、CSS 选择器或元素数组。
   * @param {number} [options.y=16] 初始下移距离，单位 px；动画会从该距离上浮到当前位置。
   * @param {number} [options.duration=0.6] 单个元素动画时长。
   * @param {number} [options.stagger=0.12] 多个元素之间的动画间隔。
   * @param {number} [options.delay=0] 动画延迟时间。
   * @param {string} [options.ease='power2.out'] GSAP ease 配置。
   * @returns {any|null} 返回 GSAP tween；初始化失败时返回 null。
   *
   * @example
   * jxsAnimate.floatUp({
   *   el: [title, image, button],
   *   stagger: 0.15,
   * });
   */
  floatUp(options = {}) {
    if (!gsap) {
      console.warn('[JxsAnimate] GSAP 未加载，floatUp 初始化失败。');
      return null;
    }

    const { el, y = 32, duration = 0.6, stagger = 0.12, delay = 0, ease = 'power2.out' } = options;
    const elements = resolveElements(el);

    if (!elements.length) {
      console.warn('[JxsAnimate] floatUp 需要传入有效的 el。', el);
      return null;
    }

    return gsap.fromTo(
      elements,
      {
        y,
        opacity: 0,
      },
      {
        y: 0,
        opacity: 1,
        duration,
        delay,
        ease,
        stagger: elements.length > 1 ? stagger : 0,
      },
    );
  }

  /**
   * 使用 GSAP ScrollTrigger 监听元素进入 / 离开视口。
   *
   * 这个方法用于替代 `Motion.inView` 一类的视口监听逻辑：
   * - 元素可见比例达到 `enterPercent` 时触发 `enterCallback`。
   * - 元素可见比例低于 `leavePercent` 时触发 `leaveCallback`。
   * - 默认只触发一次进入回调，适合进入视口后播放一次动画。
   *
   * @param {Object} [options={}] 配置项。
   * @param {Element|string} [options.el] 需要监听的 DOM 元素或 CSS 选择器。
   * @param {boolean} [options.once=true] 是否只触发一次进入回调。
   * @param {(entry: IntersectionObserverEntry) => void} [options.enterCallback] 元素进入视口时执行的回调。
   * @param {(entry: IntersectionObserverEntry) => void} [options.leaveCallback] 元素离开视口时执行的回调；`once` 为 `true` 时不会触发。
   * @param {number} [options.enterPercent=0.1] 进入触发比例，范围 0~1；例如 0.1 表示元素 10% 进入视口后触发。
   * @param {number} [options.leavePercent=enterPercent] 离开触发比例，范围 0~1；例如 0 表示元素完全离开视口后触发。
   * @param {number} [options.delay=0] 进入视口后延迟执行 `enterCallback` 的时间，单位秒。
   * @returns {() => void} 返回清理函数，调用后会销毁当前 IntersectionObserver 监听。
   *
   * @example
   * const cleanup = jxsAnimate.inView({
   *   el: '.jxs-section',
   *   once: false,
   *   enterPercent: 0.2,
   *   leavePercent: 0,
   *   enterCallback: () => {
   *     console.log('进入视口');
   *   },
   *   leaveCallback: () => {
   *     console.log('离开视口');
   *   },
   * });
   *
   * cleanup();
   */
  inView(options = {}) {
    const {
      el,
      once = true,
      enterCallback,
      leaveCallback,
      enterPercent = 0.1,
      leavePercent = enterPercent,
      delay = 0,
    } = options;

    const element = resolveElement(el);

    if (!element) {
      console.warn('[JxsAnimate] inView 需要传入有效的 el。', el);
      return noop;
    }

    if (!('IntersectionObserver' in window)) {
      console.warn('[JxsAnimate] 当前浏览器不支持 IntersectionObserver，inView 初始化失败。');
      return noop;
    }

    const shouldRunOnce = Boolean(once);
    const enterRatio = clamp(enterPercent, 0, 1, 0.1);
    const leaveRatio = clamp(leavePercent, 0, 1, enterRatio);
    const enterDelay = Math.max(Number(delay) || 0, 0);
    const thresholds = buildIntersectionThresholds(enterRatio, leaveRatio);

    let isInView = false;
    let isDestroyed = false;
    let enterTimer = 0;

    /**
     * @param {IntersectionObserverEntry} entry
     */
    const enter = (entry) => {
      if (isInView) return;

      isInView = true;

      const runEnterCallback = () => {
        enterTimer = 0;

        if (isDestroyed) return;

        enterCallback?.(entry);
      };

      if (enterDelay > 0) {
        enterTimer = window.setTimeout(runEnterCallback, enterDelay * 1000);
      } else {
        runEnterCallback();
      }

      if (shouldRunOnce) {
        observer.unobserve(element);
        observer.disconnect();
      }
    };

    /**
     * @param {IntersectionObserverEntry} entry
     */
    const leave = (entry) => {
      if (!isInView) return;

      isInView = false;

      if (enterTimer) {
        window.clearTimeout(enterTimer);
        enterTimer = 0;
      }

      leaveCallback?.(entry);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (!entry) return;

        const ratio = entry.intersectionRatio;

        if (!isInView && entry.isIntersecting && ratio >= enterRatio) {
          enter(entry);
          return;
        }

        if (!shouldRunOnce && isInView && (!entry.isIntersecting || ratio <= leaveRatio)) {
          leave(entry);
        }
      },
      {
        threshold: thresholds,
      },
    );

    observer.observe(element);

    return () => {
      isDestroyed = true;

      if (enterTimer) {
        window.clearTimeout(enterTimer);
        enterTimer = 0;
      }

      observer.unobserve(element);
      observer.disconnect();
    };
  }
}

export const jxsAnimate = new JxsAnimate();

/**
 * 最小版 GSAP ScrollTrigger 包装器。
 *
 * 这个最小版本只暴露通用滚动场景里最常用的配置：
 * - ScrollTrigger 基础配置：trigger / start / pin / scrub / markers。
 * - 滚动状态配置：itemTotal / distance / startHold / endHold。
 *
 * 设计目标：
 * - 用尽量少的 API 描述“一段滚动距离”。
 * - 将 ScrollTrigger 的 progress 转换成业务 progress。
 * - 根据滚动距离和 itemTotal 推导当前 index。
 *
 * 它适合两类场景：
 * 1. 普通 timeline：滚动时驱动 this.timeline 上定义的动画。
 * 2. 分段滚动：根据 itemTotal 得到 indexChange，例如 tab、panel、步骤切换。
 *
 * 默认会创建：
 *
 * gsap.timeline({
 *   scrollTrigger: {
 *     trigger,
 *     start: 'top top',
 *     end: () => `+=${this.totalDistance}`,
 *     pin: true,
 *     scrub: true,
 *   },
 * });
 *
 * @typedef {Object} JxsScrollTriggerOptions
 * @property {Element|string} [trigger]
 * ScrollTrigger 的触发元素。默认使用构造函数传入的 component。
 * 当 component 是整个自定义组件，但实际需要固定内部某个元素时，可以单独传 trigger。
 *
 * @property {string|number|Function} [start='top top']
 * 原生 ScrollTrigger 的 start 配置。
 *
 * @property {boolean|Element|string} [pin=true]
 * 原生 ScrollTrigger 的 pin 配置。默认固定 trigger。
 *
 * @property {boolean|number} [scrub=true]
 * 原生 ScrollTrigger 的 scrub 配置。默认让 timeline 跟随滚动进度播放。
 *
 * @property {string} [markersName]
 * @property {boolean|Object} [markers=false]
 * 原生 ScrollTrigger 的 markers 配置。开启后同时输出滚动距离调试信息。
 *
 * @property {number} [disabledOnMedia=0]
 * 小于等于该 viewport 宽度时禁用 ScrollTrigger，单位 px。
 * 例如 disabledOnMedia: 1023 表示 <=1023px 禁用，>=1024px 启用。
 *
 * @property {number} [itemTotal=0]
 * 需要拆分成多少个滚动状态。
 * 设置后会根据当前滚动距离计算 index，并在 index 变化时触发 indexChange。
 * 例如 itemTotal = 5 时，index 范围是 0、1、2、3、4。
 *
 * @property {number|Function} [distance=window.innerHeight]
 * progress 0~1 的总滚动距离，单位是 px。
 * 不包含 startHold 和 endHold。
 *
 * 例如：
 * - distance = 1000 表示滚动 1000px，progress 从 0 到 1。
 * - distance = () => window.innerHeight 表示滚动一屏。
 *
 * 当 itemTotal > 1 时，distance 会被均分到每个 index。
 * 例如 distance = 1000、itemTotal = 4：
 * - 每 250px 触发一次 indexChange。
 *
 * @property {number} [startHold=0]
 * 整段滚动开始前的额外停留距离，单位是 px。
 * 它会增加 ScrollTrigger 的 end 距离，但不会影响 progress。
 * 也就是说开始滚动 startHold 距离内，progress 仍然保持为 0。
 *
 * @property {number} [endHold=0]
 * 整段滚动结束后的额外停留距离，单位是 px。
 * 它会增加 ScrollTrigger 的 end 距离，但不会影响 progress。
 * 也就是说 progress 到 1 后，还可以继续固定一段 endHold 距离。
 *
 * @param {Element|string} component
 * 当前组件元素或 CSS 选择器。
 * 默认会作为 ScrollTrigger 的 trigger，也就是滚动触发和 pin 的元素。
 *
 * @param {JxsScrollTriggerOptions} [options={}]
 * 可选配置。
 *
 * 常用 options：
 * - trigger：覆盖默认触发元素。
 * - start：ScrollTrigger 开始位置，默认 'top top'。
 * - pin：是否固定元素，默认 true。
 * - scrub：是否让 timeline 跟随滚动播放，默认 true。
 * - markers：是否显示调试标记，默认 false。
 * - itemTotal：分段数量，用来计算 indexChange。
 * - distance：progress 0~1 的总滚动距离，单位 px。
 * - startHold：开始前停留距离，单位 px。
 * - endHold：末尾 progress 到 1 后继续固定的距离，单位 px。
 *
 * @example
 * this.scroller = new JxsScrollTrigger(this, {
 *   itemTotal: 5,
 *   distance: 1000,
 *   startHold: 300,
 *   endHold: 500,
 * });
 *
 * this.scroller.on('progress', percent => {
 *   // percent: 0 ~ 1，startHold 和 endHold 不会影响这个值
 * });
 *
 * this.scroller.on('indexChange', index => {
 *   // index: 0 ~ itemTotal - 1
 * });
 *
 * this.scroller.timeline.to(this.querySelector('.track'), {
 *   xPercent: -400,
 *   ease: 'none',
 * });
 */
export class JxsScrollTrigger {
  /** @type {boolean} */
  #disabled = false;

  /** @type {boolean} */
  #isDestroying = false;

  /** @type {boolean} */
  static #needsRefresh = false;

  /**
   * 调度全局 ScrollTrigger 刷新。
   *
   * 用 setTimeout(0) 确保所有同步创建的实例都初始化完了才执行。
   * 解决多个 pin 元素互相影响的问题：前面的 pinSpacing 会影响后面元素的位置。
   */
  static #scheduleRefresh() {
    if (JxsScrollTrigger.#needsRefresh) return;

    JxsScrollTrigger.#needsRefresh = true;

    setTimeout(() => {
      JxsScrollTrigger.#needsRefresh = false;

      if (typeof ScrollTrigger?.sort === 'function') {
        ScrollTrigger.sort();
      }

      if (typeof ScrollTrigger?.refresh === 'function') {
        ScrollTrigger.refresh();
      }
    }, 0);
  }

  constructor(
    /** @type {Element|string} */ component,
    /** @type {JxsScrollTriggerOptions} */ options = {},
  ) {
    /** @type {Element|string} */
    this.component = component;

    /** @type {JxsScrollTriggerOptions} */
    this.options = {
      trigger: component,
      start: 'top top',
      pin: true,
      scrub: true,
      markersName: '',
      markers: false,
      disabledOnMedia: 0,
      itemTotal: 0,
      distance: () => window.innerHeight,
      startHold: 0,
      endHold: 0,
      ...options,
    };

    /** @type {Map<string, Set<Function>>} */
    this.listeners = new Map();

    /** @type {Array<{ start: number, end: number, progress: Function, currentProgress: number|null }>} */
    this.segmentProgressList = [];

    /** @type {Element|null} */
    this.trigger = null;

    /** @type {any} */
    this.timeline = null;

    /** @type {any} */
    this.scrollTrigger = null;

    /** @type {any} */
    this.media = null;

    this.progress = 0;
    this.currentIndex = 0;

    this.create();
  }

  /**
   * @returns {number}
   */
  get itemTotal() {
    return toNonNegativeInteger(this.options.itemTotal, 0);
  }

  /**
   * progress 0~1 的总滚动距离，单位 px。
   * 不包含 startHold 和 endHold。
   *
   * @returns {number}
   */
  get distance() {
    return toNonNegativeNumber(resolveValue(this.options.distance, this), window.innerHeight || 1);
  }

  /**
   * @returns {number}
   */
  get startHold() {
    return toNonNegativeNumber(this.options.startHold, 0);
  }

  /**
   * @returns {number}
   */
  get endHold() {
    return toNonNegativeNumber(this.options.endHold, 0);
  }

  /**
   * 不包含 startHold 和 endHold 的业务滚动距离。
   * 始终等于 distance。
   *
   * @returns {number}
   */
  get contentDistance() {
    return this.distance;
  }

  /**
   * 包含 startHold 和 endHold 的 ScrollTrigger 总滚动距离。
   *
   * @returns {number}
   */
  get totalDistance() {
    return this.startHold + this.contentDistance + this.endHold;
  }

  /**
   * 注册事件。
   *
   * @param {'progress'|'indexChange'|'disabledChange'|string} eventName 事件名称。
   * @param {Function} callback 回调。
   * @returns {() => void} 取消监听函数。
   */
  on(eventName, callback) {
    if (typeof callback !== 'function') return noop;

    let listeners = this.listeners.get(eventName);

    if (!listeners) {
      listeners = new Set();
      this.listeners.set(eventName, listeners);
    }

    listeners.add(callback);

    if (eventName === 'disabledChange') {
      callback(this.#disabled);
    }

    return () => {
      listeners.delete(callback);
    };
  }

  /**
   * 注册一段区间进度回调。
   *
   * start / end 使用 0 ~ 100 的百分比数值。
   * 当当前滚动进度进入 start ~ end 区间时，progress 回调会收到区间内 0 ~ 1 的进度。
   *
   * @param {{ start?: number, end?: number, progress?: (progress: number) => void }} [options={}] 配置项。
   * @returns {() => void} 取消当前区间进度监听。
   */
  addSegmentProgress(options = {}) {
    const { start = 0, end = 100, progress } = options;

    if (typeof progress !== 'function') return noop;

    let safeStart = clamp(Number(start), 0, 100, 0);
    let safeEnd = clamp(Number(end), 0, 100, 100);

    if (safeStart === safeEnd) return noop;

    if (safeStart > safeEnd) {
      [safeStart, safeEnd] = [safeEnd, safeStart];
    }

    const segmentProgress = {
      start: safeStart,
      end: safeEnd,
      progress,
      currentProgress: null,
    };

    this.segmentProgressList.push(segmentProgress);
    this.emitSegmentProgress(false, segmentProgress);

    return () => {
      const index = this.segmentProgressList.indexOf(segmentProgress);

      if (index !== -1) {
        this.segmentProgressList.splice(index, 1);
      }
    };
  }

  /**
   * 创建 ScrollTrigger timeline。
   */
  create() {
    if (!gsap || !ScrollTrigger) {
      console.warn('[JxsScrollTrigger] GSAP 或 ScrollTrigger 未加载。');
      return;
    }

    const trigger = resolveElement(this.options.trigger ?? this.component);

    if (!trigger) {
      console.warn('[JxsScrollTrigger] 需要传入有效的 trigger。', this.options.trigger);
      return;
    }

    this.trigger = trigger;

    const disabledOnMedia = toNonNegativeInteger(this.options.disabledOnMedia, 0);

    if (disabledOnMedia > 0 && typeof gsap.matchMedia === 'function') {
      this.#setDisabled(true, false);
      this.media = gsap.matchMedia();

      this.media.add(`(min-width: ${disabledOnMedia + 1}px)`, () => {
        this.createTimeline(trigger);
        this.#setDisabled(false);

        return () => {
          this.killTimeline();
          this.#setDisabled(true);
        };
      });

      return;
    }

    this.#setDisabled(false, false);
    this.createTimeline(trigger);
  }

  /**
   * 创建 timeline 和 ScrollTrigger。
   *
   * @param {Element} trigger
   */
  createTimeline(trigger) {
    this.killTimeline();

    this.timeline = gsap.timeline({
      scrollTrigger: {
        trigger,
        start: this.options.start ?? 'top top',
        end: () => `+=${this.totalDistance}`,
        pin: this.options.pin ?? true,
        scrub: this.options.scrub ?? true,
        markers: this.options.markers ?? false,
        invalidateOnRefresh: true,
        onUpdate: (/** @type {any} */ self) => this.update(self),
        onRefresh: (/** @type {any} */ self) => this.update(self),
      },
    });

    this.scrollTrigger = this.timeline.scrollTrigger;

    if (this.scrollTrigger) {
      this.update(this.scrollTrigger);
    }

    JxsScrollTrigger.#scheduleRefresh();
  }

  /**
   * 销毁 timeline 和 ScrollTrigger，但保留事件监听。
   */
  killTimeline() {
    if (this.scrollTrigger) {
      this.scrollTrigger.kill();
    }

    if (this.timeline) {
      this.timeline.kill();
    }

    this.scrollTrigger = null;
    this.timeline = null;
  }

  /**
   * 根据 ScrollTrigger 进度更新 progress 和 index。
   *
   * @param {any} self ScrollTrigger 实例。
   */
  update(self) {
    const rawProgress = clamp(self?.progress ?? 0, 0, 1, 0);
    
    const rawDistance = rawProgress * this.totalDistance;
    const distance = clamp(rawDistance - this.startHold, 0, this.contentDistance, 0);

    this.progress =
      this.contentDistance > 0 ? clamp(distance / this.contentDistance, 0, 1, 0) : rawProgress;

    const index = this.getIndex(distance);

    this.logDebugInfo(self, {
      rawProgress,
      rawDistance,
      distance,
      index,
    });

    // 当 rawProgress 达到 1 时，无论 isActive 状态如何，都强制触发终点事件
    if (rawProgress === 1) {
      this.emit('progress', 1);
      this.emitSegmentProgress(true);
      
      if (index !== this.currentIndex) {
        this.currentIndex = index;
        this.emit('indexChange', index);
      }
      return;
    }

    if (self?.isActive === true) {
      this.emit('progress', this.progress);
      this.emitSegmentProgress();
  
      if (index !== this.currentIndex) {
        this.currentIndex = index;
        this.emit('indexChange', index);
      }
    }
  }

  /**
   * 输出滚动距离调试信息。
   *
   * @param {any} self ScrollTrigger 实例。
   * @param {{ rawProgress: number, rawDistance: number, distance: number, index: number }} data
   */
  logDebugInfo(self, data) {
    if (!this.options.markers) return;

    const currentScroll =
      typeof self?.scroll === 'function' ? self.scroll() : window.scrollY || window.pageYOffset || 0;
    const triggerStart = Number(self?.start ?? 0);
    const triggerEnd = Number(self?.end ?? 0);
    const contentStart = this.startHold;
    const contentEnd = this.startHold + this.contentDistance;
    const phase =
      data.rawDistance < contentStart
        ? 'startHold'
        : data.rawDistance > contentEnd
          ? 'endHold'
          : 'content';

    const actualScrollDistance = triggerEnd - triggerStart;
    const pinSpacing = actualScrollDistance - this.totalDistance;

    console.log(
      `[${this.options.markersName || 'JxsScrollTrigger'}] phase:${phase}` +
      ` scrollY:${Math.round(currentScroll)}` +
      ` trigger:${Math.round(triggerStart)}~${Math.round(triggerEnd)}` +
      ` scrollFromStart:${Math.round(currentScroll - triggerStart)}` +
      ` rawProgress:${data.rawProgress.toFixed(4)}` +
      ` rawDistance:${Math.round(data.rawDistance)}` +
      ` startHold:${Math.round(this.startHold)}` +
      ` contentDistance:${Math.round(this.contentDistance)}` +
      ` contentProgress:${this.progress.toFixed(4)}` +
      ` endHold:${Math.round(this.endHold)}` +
      ` totalDistance:${Math.round(this.totalDistance)}` +
      ` actualScrollDistance:${Math.round(actualScrollDistance)}` +
      ` pinSpacing:${Math.round(pinSpacing)}` +
      ` itemTotal:${this.itemTotal}` +
      ` index:${data.index}`
    );
  }

  /**
   * 根据当前滚动进度派发区间进度回调。
   *
   * @param {boolean} [forceEnd=false] 是否强制触发所有区间的终点事件。
   * @param {{ start: number, end: number, progress: Function, currentProgress: number|null }} [targetSegment]
   */
  emitSegmentProgress(forceEnd = false, targetSegment) {
    const currentProgress = clamp(this.progress * 100, 0, 100, 0);
    const segmentProgressList = targetSegment ? [targetSegment] : this.segmentProgressList;

    for (const segmentProgress of segmentProgressList) {
      const { start, end, progress } = segmentProgress;
      const previousProgress = segmentProgress.currentProgress;

      // 如果强制触发终点，直接设置为 1
      if (forceEnd) {
        if (previousProgress !== null && previousProgress !== 1) {
          segmentProgress.currentProgress = 1;
          progress(1);
        }
        continue;
      }

      if (currentProgress < start) {
        if (previousProgress !== null && previousProgress !== 0) {
          segmentProgress.currentProgress = 0;
          progress(0);
        }

        continue;
      }

      if (currentProgress >= end) {
        if (previousProgress !== null && previousProgress !== 1) {
          segmentProgress.currentProgress = 1;
          progress(1);
        }

        continue;
      }

      const nextProgress = clamp((currentProgress - start) / (end - start), 0, 1, 0);

      if (previousProgress === nextProgress) continue;

      segmentProgress.currentProgress = nextProgress;
      progress(nextProgress);
    }
  }

  /**
   * 根据滚动进度计算当前 index。
   *
   * @param {number} distance 当前业务滚动距离。
   * @returns {number}
   */
  getIndex(distance) {
    if (this.itemTotal <= 1 || this.distance <= 0) {
      return 0;
    }

    const progress = clamp(distance / this.distance, 0, 1, 0);
    return clamp(Math.floor(progress * this.itemTotal), 0, this.itemTotal - 1, 0);
  }

  /**
   * @param {Element} trigger
   */

  /**
   * 销毁 timeline 和事件监听。
   */
  destroy() {
    this.#isDestroying = true;

    if (this.media) {
      this.media.revert();
      this.media = null;
    }

    this.killTimeline();

    this.trigger = null;
    this.segmentProgressList = [];
    this.listeners.clear();
  }

  /**
   * 设置内部禁用状态，并在状态变化时发布 disabledChange。
   *
   * @param {boolean} disabled 是否禁用。
   * @param {boolean} [shouldEmit=true] 是否触发事件。
   */
  #setDisabled(disabled, shouldEmit = true) {
    const nextDisabled = Boolean(disabled);

    if (nextDisabled === this.#disabled) return;

    this.#disabled = nextDisabled;

    if (shouldEmit && !this.#isDestroying) {
      this.emit('disabledChange', nextDisabled);
    }
  }

  /**
   * 触发事件。
   *
   * @param {string} eventName 事件名称。
   * @param {...any} args 参数。
   */
  emit(eventName, ...args) {
    const listeners = this.listeners.get(eventName);

    if (!listeners?.size) return;

    for (const callback of listeners) {
      callback(...args);
    }
  }
}

/**
 * 生成 IntersectionObserver threshold 配置。
 *
 * @param {...number} ratios
 * @returns {number[]}
 */
function buildIntersectionThresholds(...ratios) {
  return Array.from(new Set([0, ...ratios, 1]))
    .filter((ratio) => Number.isFinite(ratio))
    .map((ratio) => clamp(ratio, 0, 1, 0))
    .sort((a, b) => a - b);
}

/**
 * @param {*} value
 * @param {*} context
 * @returns {*}
 */
function resolveValue(value, context) {
  return typeof value === 'function' ? value.call(context, context) : value;
}

/**
 * @param {*} value
 * @param {number} defaultValue
 * @returns {number}
 */
function toNonNegativeNumber(value, defaultValue) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return defaultValue;
  }

  return Math.max(number, 0);
}

/**
 * @param {*} value
 * @param {number} defaultValue
 * @returns {number}
 */
function toNonNegativeInteger(value, defaultValue) {
  return Math.floor(toNonNegativeNumber(value, defaultValue));
}